import React, { useState, useEffect, useRef } from "react";
import config from "../config";
import orca from "../Images/ORCA.png";
import UserMessenger from "./UserMessenger";
import AIChatBot from "./Aichatbot";

/* ─── Animated SVG Icon Components ──────────────────────────────────────────── */
const IconTicket = ({ animated }) => (
  <svg className={`w-8 h-8 ${animated ? "icon-bounce" : ""}`} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="g1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
    <path className={animated ? "icon-draw" : ""} d="M12 4v16m8-8H4" stroke="url(#g1)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="10" stroke="url(#g1)" strokeWidth="1.5" opacity="0.3" className={animated ? "icon-pulse-ring" : ""} />
  </svg>
);

const IconList = ({ animated }) => (
  <svg className={`w-8 h-8 ${animated ? "icon-bounce" : ""}`} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="g2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#818cf8" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="url(#g2)" strokeWidth="1.5" className={animated ? "icon-draw" : ""} />
    <path d="M7 8h10M7 12h10M7 16h6" stroke="url(#g2)" strokeWidth="2" strokeLinecap="round" className={animated ? "icon-lines" : ""} />
  </svg>
);

const IconUser = ({ animated }) => (
  <svg className={`w-8 h-8 ${animated ? "icon-bounce" : ""}`} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="g3" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="100%" stopColor="#10b981" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="8" r="4" stroke="url(#g3)" strokeWidth="2" className={animated ? "icon-draw" : ""} />
    <path d="M5 20a7 7 0 0114 0" stroke="url(#g3)" strokeWidth="2" strokeLinecap="round" className={animated ? "icon-draw" : ""} />
  </svg>
);

const IconMessage = ({ animated }) => (
  <svg className={`w-8 h-8 ${animated ? "icon-bounce" : ""}`} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="g4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f472b6" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" stroke="url(#g4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={animated ? "icon-draw" : ""} />
    <circle cx="8" cy="10" r="1" fill="#f472b6" className={animated ? "icon-dot1" : ""} />
    <circle cx="12" cy="10" r="1" fill="#f472b6" className={animated ? "icon-dot2" : ""} />
    <circle cx="16" cy="10" r="1" fill="#f472b6" className={animated ? "icon-dot3" : ""} />
  </svg>
);

const IconSun = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
      <line key={i} x1="12" y1="3" x2="12" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        style={{ transformOrigin: "12px 12px", transform: `rotate(${deg}deg)` }} />
    ))}
  </svg>
);

const IconMoon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
    <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserDashboard = ({ setIsLoggedIn, setCurrentPage }) => {
  const [userData, setUserData] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [currentView, setCurrentView] = useState("dashboard");
  const [isDarkMode, setIsDarkMode] = useState(true);
  const messengerRef = useRef(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserData(user);
    if (user) {
      if (user.googleProfilePicture) {
        setProfilePicture(user.googleProfilePicture);
      } else {
        loadProfilePicture(user.username);
      }
    }
  }, []);
  const [showRealChat, setShowRealChat] = useState(false);

  const loadProfilePicture = async (username) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/Users/get-profile-picture/${username}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setProfilePicture(url);
        const user = JSON.parse(localStorage.getItem("user"));
        user.localProfilePicture = url;
        localStorage.setItem("user", JSON.stringify(user));
      }
    } catch (error) {
      console.error("Error loading profile picture:", error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setCurrentPage("home");
  };

  if (!userData) {
    return (
      <div className={`min-h-screen ${isDarkMode ? "bg-gray-950" : "bg-slate-50"} flex items-center justify-center`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
          <div className="absolute inset-2 w-12 h-12 rounded-full border-4 border-blue-400/20 border-t-blue-400 animate-spin" style={{ animationDirection: "reverse", animationDuration: "0.6s" }} />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        * { font-family: 'DM Sans', sans-serif; }
        h1, h2, h3, .font-display { font-family: 'Syne', sans-serif; }

        .icon-bounce { transition: transform 0.3s cubic-bezier(.34,1.56,.64,1); }
        .icon-bounce:hover, .quick-card:hover .icon-bounce { transform: scale(1.2) rotate(-5deg); }

        @keyframes draw { from { stroke-dashoffset: 200; opacity: 0; } to { stroke-dashoffset: 0; opacity: 1; } }
        @keyframes pulse-ring { 0%,100% { r: 10; opacity: 0.3; } 50% { r: 11; opacity: 0.6; } }
        @keyframes dot-bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-3px); } }
        @keyframes float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes glow-pulse { 0%,100% { box-shadow: 0 0 20px rgba(59,130,246,0.3); } 50% { box-shadow: 0 0 40px rgba(59,130,246,0.6); } }

        .icon-draw { stroke-dasharray: 200; animation: draw 0.8s ease forwards; }
        .icon-pulse-ring { animation: pulse-ring 2s ease-in-out infinite; }
        .icon-dot1 { animation: dot-bounce 1.2s ease-in-out infinite; }
        .icon-dot2 { animation: dot-bounce 1.2s ease-in-out infinite 0.2s; }
        .icon-dot3 { animation: dot-bounce 1.2s ease-in-out infinite 0.4s; }
        .icon-lines { stroke-dasharray: 100; animation: draw 1s ease forwards 0.3s; opacity: 0; animation-fill-mode: forwards; }

        .animate-slide-up { animation: slide-up 0.4s ease forwards; }
        .animate-scale-in { animation: scale-in 0.3s ease forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-glow { animation: glow-pulse 2s ease-in-out infinite; }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }

        .quick-card {
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(.34,1.56,.64,1);
        }
        .quick-card::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.3s;
          background: radial-gradient(circle at 50% 0%, rgba(59,130,246,0.15) 0%, transparent 70%);
        }
        .quick-card:hover::before { opacity: 1; }
        .quick-card:hover { transform: translateY(-4px) scale(1.02); box-shadow: 0 20px 40px -12px rgba(59,130,246,0.3); }
        .quick-card:active { transform: translateY(-1px) scale(1.00); }

        .stat-card { transition: all 0.25s ease; }
        .stat-card:hover { transform: translateY(-2px); }

        .nav-btn-active { position: relative; }
        .nav-btn-active::after { content: ''; position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: #3b82f6; }

        .profile-ring { background: conic-gradient(from 0deg, #3b82f6, #60a5fa, #93c5fd, #3b82f6); animation: spin 4s linear infinite; border-radius: 50%; padding: 2px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .dropdown-menu { animation: scale-in 0.2s ease; transform-origin: top right; }

        .mesh-bg {
          background-image: 
            radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 60% 80%, rgba(16,185,129,0.05) 0%, transparent 50%);
        }

        .glass-card {
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .info-row { transition: all 0.2s ease; border-radius: 10px; padding: 10px 12px; margin: -10px -12px; }
        .info-row:hover { background: rgba(59,130,246,0.06); }

        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(59,130,246,0.3); border-radius: 99px; }
      `}</style>

      <div className={`min-h-screen transition-colors duration-500 mesh-bg ${isDarkMode ? "bg-gray-950" : "bg-slate-50"}`}>

        {/* Dark Mode Toggle */}
        <div className="fixed top-5 right-5 z-20 group">
          <button
            onClick={toggleDarkMode}
            className={`relative w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-110 active:scale-95 ${isDarkMode ? "bg-gray-800/90 text-yellow-400 hover:bg-gray-700 border border-gray-700/60" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200/80"}`}
          >
            <span className={`transition-all duration-300 ${isDarkMode ? "rotate-0 opacity-100" : "rotate-180 opacity-0 absolute"}`}><IconSun /></span>
            <span className={`transition-all duration-300 ${!isDarkMode ? "rotate-0 opacity-100" : "rotate-180 opacity-0 absolute"}`}><IconMoon /></span>
          </button>
          <div className={`absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all pointer-events-none text-xs font-medium ${isDarkMode ? "bg-gray-800 text-white border border-gray-700" : "bg-gray-900 text-white"}`}>
            {isDarkMode ? "Light Mode" : "Dark Mode"}
          </div>
        </div>

        {/* Header */}
        <header className={`sticky top-0 z-10 transition-all duration-300 ${isDarkMode ? "bg-gray-950/80 border-gray-800/60" : "bg-white/80 border-slate-200/60"} border-b glass-card shadow-sm`}>
          <div className="container mx-auto px-5 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center gap-4">
                <div className={`px-3 py-2 rounded-xl transition-colors ${isDarkMode ? "bg-white/10" : "bg-gray-50 border border-gray-100"}`}>
                  <img src={orca} alt="ORCA" className="h-9 w-auto object-contain" />
                </div>
                <div>
                  <p className={`text-xs font-medium tracking-widest uppercase ${isDarkMode ? "text-blue-400/70" : "text-blue-500/70"}`}>User Portal</p>
                  <p className={`font-display font-bold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}>Support Desk</p>
                </div>
              </div>

              {/* Profile */}
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={`flex items-center gap-3 pl-3 pr-4 py-2 rounded-2xl transition-all ${isDarkMode ? "bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700/60" : "bg-slate-100 hover:bg-slate-200 border border-slate-200/60"}`}
                >
                  <div className="relative">
                    {profilePicture ? (
                      <div className="relative w-9 h-9">
                        <div className="profile-ring absolute inset-0 scale-110 opacity-80" />
                        <img src={profilePicture} alt="Profile" className="relative w-9 h-9 rounded-full object-cover z-10" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                        {userData.fullNames.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-gray-950 z-20 animate-pulse" />
                  </div>
                  <div className="hidden md:block text-left">
                    <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>Welcome back</p>
                    <p className={`font-semibold text-sm leading-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>{userData.fullNames}</p>
                  </div>
                  <svg className={`w-4 h-4 transition-transform duration-300 ${isDarkMode ? "text-gray-500" : "text-gray-400"} ${isMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isMenuOpen && (
                  <div className={`dropdown-menu absolute right-0 mt-2 w-64 rounded-2xl overflow-hidden shadow-2xl border ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-slate-200"}`}>
                    <div className="relative p-4 overflow-hidden" style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)" }}>
                      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10" />
                      <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/5" />
                      <div className="relative flex items-center gap-3">
                        {profilePicture ? (
                          <img src={profilePicture} alt="Profile" className="w-12 h-12 rounded-xl object-cover border-2 border-white/40 shadow-lg" />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {userData.fullNames.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-display font-bold text-white">{userData.fullNames}</p>
                          <p className="text-xs text-blue-100/80">{userData.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-2">
                      {[
                        { icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z", label: "My Profile", view: "profile" },
                        { icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z", label: "Settings", view: "settings" },
                      ].map((item) => (
                        <button key={item.view} onClick={() => { setCurrentView(item.view); setIsMenuOpen(false); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${isDarkMode ? "text-gray-300 hover:text-white hover:bg-white/5" : "text-gray-600 hover:text-gray-900 hover:bg-slate-50"}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-gray-800" : "bg-slate-100"}`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} /></svg>
                          </div>
                          {item.label}
                        </button>
                      ))}
                      <div className={`my-2 border-t ${isDarkMode ? "border-gray-800" : "border-slate-100"}`} />
                      <button onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        </div>
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="container mx-auto px-5 py-8 animate-slide-up">
          {currentView === "dashboard" && <DashboardView userData={userData} setCurrentView={setCurrentView} isDarkMode={isDarkMode} onOpenMessenger={() => messengerRef.current?.openExpanded()} />}
          {currentView === "create-ticket" && <CreateTicketView userData={userData} setCurrentView={setCurrentView} isDarkMode={isDarkMode} />}
          {currentView === "my-tickets" && <MyTicketsView userData={userData} setCurrentView={setCurrentView} isDarkMode={isDarkMode} onSelectTicket={setSelectedTicket} />}
          {currentView === "profile" && <ProfileView userData={userData} profilePicture={profilePicture} loadProfilePicture={loadProfilePicture} setCurrentView={setCurrentView} isDarkMode={isDarkMode} />}
          {currentView === "settings" && <SettingsView userData={userData} setCurrentView={setCurrentView} isDarkMode={isDarkMode} />}
        </div>

        {!showRealChat
          ? <AIChatBot userData={userData} onHandoff={() => setShowRealChat(true)} />
          : <UserMessenger userData={userData} ref={messengerRef} />
        }
        {selectedTicket && (
          <TicketDetailModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)}
            getStatusStyle={s => ({ Open: "bg-blue-500/15 text-blue-400 border-blue-500/30", Pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", "In Progress": "bg-indigo-500/15 text-indigo-400 border-indigo-500/30", Resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Closed: "bg-gray-500/15 text-gray-400 border-gray-500/30" }[s] || "bg-slate-500/15 text-slate-400 border-slate-500/30")}
            getPriorityStyle={p => ({ Low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", High: "bg-orange-500/15 text-orange-400 border-orange-500/30", Critical: "bg-red-500/15 text-red-400 border-red-500/30" }[p] || "bg-slate-500/15 text-slate-400 border-slate-500/30")}
            formatDate={d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}
            formatDateTime={d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"} />
        )}
      </div>
    </>
  );
};

/* ─── Dashboard View ─────────────────────────────────────────────────────────── */
const DashboardView = ({ userData, setCurrentView, isDarkMode, onOpenMessenger }) => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <>
      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-3xl p-8 mb-8 shadow-2xl animate-glow" style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #1e40af 100%)" }}>
        <div className="absolute inset-0 animate-shimmer" />
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: "radial-gradient(circle, white, transparent)", transform: "translate(30%, -30%)" }} />
        <div className="absolute bottom-0 left-1/3 w-48 h-48 rounded-full opacity-5" style={{ background: "radial-gradient(circle, white, transparent)", transform: "translate(-50%, 50%)" }} />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-blue-200 text-xs font-medium tracking-widest uppercase">Online & Ready</span>
          </div>
          <h1 className="font-display text-4xl font-bold text-white mb-2 leading-tight">
            Welcome back, <span className="text-blue-200">{userData.fullNames.split(" ")[0]}</span>! 👋
          </h1>
          <p className="text-blue-100/80 text-base">Submit and track your support tickets with ease.</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {[
          { id: "create", icon: <IconTicket animated={hoveredCard === "create"} />, label: "Create Ticket", desc: "Submit a new request", accent: "from-blue-500/15 to-blue-600/5", border: "border-blue-500/20", glow: "hover:border-blue-500/50", action: () => setCurrentView("create-ticket") },
          { id: "tickets", icon: <IconList animated={hoveredCard === "tickets"} />, label: "My Tickets", desc: "Track your requests", accent: "from-indigo-500/15 to-indigo-600/5", border: "border-indigo-500/20", glow: "hover:border-indigo-500/50", action: () => setCurrentView("my-tickets") },
          { id: "profile", icon: <IconUser animated={hoveredCard === "profile"} />, label: "My Profile", desc: "Update your info", accent: "from-emerald-500/15 to-emerald-600/5", border: "border-emerald-500/20", glow: "hover:border-emerald-500/50", action: () => setCurrentView("profile") },
          { id: "messages", icon: <IconMessage animated={hoveredCard === "messages"} />, label: "Messages", desc: "Chat with support", accent: "from-pink-500/15 to-pink-600/5", border: "border-pink-500/20", glow: "hover:border-pink-500/50", action: () => onOpenMessenger() },
        ].map((card) => (
          <button
            key={card.id}
            onClick={card.action}
            onMouseEnter={() => setHoveredCard(card.id)}
            onMouseLeave={() => setHoveredCard(null)}
            className={`quick-card text-left p-6 rounded-2xl border bg-gradient-to-br ${card.accent} ${card.border} ${card.glow} ${isDarkMode ? "bg-gray-900/60" : "bg-white/80"} border transition-all`}
          >
            <div className="mb-4">{card.icon}</div>
            <p className={`font-display font-bold text-base mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}>{card.label}</p>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{card.desc}</p>
          </button>
        ))}
      </div>

      {/* Info + Recent side by side */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Info Card */}
        <div className={`rounded-2xl p-6 border shadow-sm transition-colors ${isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60"}`}>
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            </div>
            <h2 className={`font-display font-bold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}>Your Information</h2>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {[
              { label: "Full Name", value: userData.fullNames },
              { label: "Email", value: userData.email },
              { label: "Username", value: userData.username },
              { label: "Phone", value: userData.phone || "Not provided" },
              { label: "Language", value: userData.language === "en" ? "English" : userData.language },
              { label: "User ID", value: userData.identificationCode },
            ].map((item) => (
              <div key={item.label} className="info-row">
                <p className={`text-xs font-medium mb-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.label}</p>
                <p className={`text-sm font-semibold truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Tickets */}
        <div className={`rounded-2xl p-6 border shadow-sm transition-colors ${isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60"}`}>
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-xl bg-indigo-500/15 flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            </div>
            <h2 className={`font-display font-bold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}>Recent Tickets</h2>
          </div>
          <div className="flex flex-col items-center justify-center h-48 text-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${isDarkMode ? "bg-gray-800" : "bg-slate-100"} animate-float`}>
              <svg className={`w-8 h-8 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <p className={`font-semibold mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>No tickets yet</p>
            <p className={`text-sm mb-5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Create your first ticket to get started</p>
            <button onClick={() => setCurrentView("create-ticket")}
              className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-blue-500/30">
              Create First Ticket
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Create Ticket View ─────────────────────────────────────────────────────── */
const CreateTicketView = ({ userData, setCurrentView, isDarkMode }) => {
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]); // multi-file, max 6
  const [requestTypes, setRequestTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const MAX_FILES = 6;
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/RequestType`)
      .then(r => r.json()).then(setRequestTypes).catch(console.error);

    fetch(`${config.API_BASE_URL}/Category`)
      .then(r => r.json()).then(setCategories).catch(console.error);
  }, []);

  const [form, setForm] = useState({
    Subject: "", Name: userData.fullNames, Contact: userData.phone || "",
    Email: userData.email, Category: "", Description: "", Priority: "Low",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const generateTicketNumber = () => {
    const now = new Date();
    return `TICKET-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}-${String(now.getMilliseconds()).padStart(3, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.Subject || !form.Email || !form.Category || !form.Description || !form.Priority) {
      alert("Please fill in all required fields!"); return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      const ticketNumber = generateTicketNumber();
      const now = new Date().toISOString();
      const deadline = new Date(); deadline.setDate(deadline.getDate() + 7);
      const fields = { TicketNumber: ticketNumber, Subject: form.Subject, Name: form.Name, Contact: form.Contact || "N/A", Email: form.Email, Category: form.Category, Description: form.Description, Priority: form.Priority, UserId: userData.username, Status: "Open", Notes: "Created via User Portal", Subtask: "None", Followup: "false", Done: "false", Team: "General Support", Origin: "User Portal", Tags: "user-submission", InternalNotes: `Submitted by ${userData.fullNames}`, DescriptionNotes: "User ticket", ActivityDescription: `Ticket created at ${new Date().toLocaleString()}`, CreatedAt: now, Timestamp: now, DateStarted: now, DateUpdated: now, Deadline: deadline.toISOString(), TimeSpent: "0", TimeRemaining: "0", TimeGoal: "48 hours", Calendar: "Standard Hours", JQL: `priority = ${form.Priority}`, Condition: "New", OpenIncidents: "0", MyIncidents: "0", MajorIncidents: "0", Components: "General", Summary: form.Subject, Reporter: userData.fullNames, Estimate: "TBD", Linked: "None", Severity: "Sev-3", Urgency: "Medium", IssueType: "General Request", Affected: "N/A", Assignees: "Unassigned", WorkDescription: "Pending review", OpenDescription: `Opened on ${new Date().toLocaleDateString()}`, PendingDescription: "", CompleteDescription: "", ResolvedDescription: "", CloseDescription: "", Reason: "New submission" };
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${config.API_BASE_URL}/Tickets`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Failed to create ticket");

      // Upload attachments to TicketAttachments API
      if (attachments.length > 0) {
        try {
          const attFd = new FormData();
          attachments.forEach(f => attFd.append("files", f));
          await fetch(`${config.API_BASE_URL}/TicketAttachments/${ticketNumber}/upload`, { method: "POST", body: attFd });
        } catch { /* non-fatal */ }
      }

      let emailSent = false;
      try {
        const emailRes = await fetch(`${config.API_BASE_URL}/Email/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ toEmail: form.Email, toName: form.Name, subject: `[Ticket Received] ${ticketNumber}`, body: `Hi ${form.Name},\n\nYour ticket ${ticketNumber} has been received.\n\nBest regards,\nOrca Helpdesk Team` }) });
        emailSent = emailRes.ok;
      } catch { }
      alert(`✅ Ticket Created!\n\nTicket: ${ticketNumber}\n${attachments.length > 0 ? `📎 ${attachments.length} file(s) attached.\n` : ""}${emailSent ? `📧 Confirmation sent to ${form.Email}.` : `⚠️ Ticket created but email failed.`}`);
      setCurrentView("my-tickets");
    } catch (err) { alert(`❌ Error: ${err.message}`); }
    finally { setLoading(false); }
  };

  const card = isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60";
  const label = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = isDarkMode ? "bg-gray-800/80 border-gray-700/60 text-white placeholder-gray-500 focus:bg-gray-800" : "bg-white border-slate-200 text-gray-900 placeholder-slate-400 focus:bg-white";

  return (
    <div className="max-w-2xl mx-auto animate-slide-up">
      <button onClick={() => setCurrentView("dashboard")} className={`mb-6 flex items-center gap-2 text-sm font-medium transition-all hover:-translate-x-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Dashboard
      </button>

      <div className={`${card} border rounded-3xl p-8 shadow-sm`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-blue-500/15 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          </div>
          <div>
            <h2 className={`font-display font-bold text-2xl ${isDarkMode ? "text-white" : "text-gray-900"}`}>New Support Ticket</h2>
            <p className={`text-sm ${label}`}>Fill out the form to submit your request</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Request Type" required>
            <select name="Subject" value={form.Subject} onChange={handleChange} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all ${inputCls}`} required>
              <option value="">Select request type...</option>
              {requestTypes.map((rt) => <option key={rt.id} value={rt.requestTypeName}>{rt.requestTypeName}</option>)}
            </select>
          </FormField>

          <FormField label="Name">
            <input type="text" name="Name" value={form.Name} className={`w-full px-4 py-3 border rounded-xl outline-none opacity-60 cursor-not-allowed ${isDarkMode ? "bg-gray-800/50 border-gray-700/40 text-gray-300" : "bg-slate-50 border-slate-200 text-gray-600"}`} readOnly />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Contact Number">
              <input type="tel" name="Contact" value={form.Contact} onChange={handleChange} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all ${inputCls}`} placeholder="09XXXXXXXXX" />
            </FormField>
            <FormField label="Email" required>
              <input type="email" name="Email" value={form.Email} className={`w-full px-4 py-3 border rounded-xl outline-none opacity-60 cursor-not-allowed ${isDarkMode ? "bg-gray-800/50 border-gray-700/40 text-gray-300" : "bg-slate-50 border-slate-200 text-gray-600"}`} readOnly required />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Category" required>
              <select name="Category" value={form.Category} onChange={handleChange} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all ${inputCls}`} required>
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.id} value={c.categoryName}>{c.categoryName}</option>)}
              </select>
            </FormField>
            <FormField label="Priority" required>
              <select name="Priority" value={form.Priority} onChange={handleChange} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all ${inputCls}`} required>
                {["Low", "Medium", "High"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
          </div>

          <FormField label="Description" required>
            <textarea name="Description" value={form.Description} onChange={handleChange} rows={5} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 outline-none transition-all resize-none ${inputCls}`} placeholder="Describe your issue in detail..." required />
          </FormField>

          {/* Multi-file Attachment (max 6) */}
          <FormField label={`Attachments ${attachments.length > 0 ? `(${attachments.length}/${MAX_FILES})` : `(max ${MAX_FILES})`}`}>
            {/* Drop zone — only show if not at max */}
            {attachments.length < MAX_FILES && (
              <label
                htmlFor="file-upload"
                className={`flex flex-col items-center justify-center w-full py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${isDarkMode ? "border-gray-700 hover:border-blue-500/60 bg-gray-800/30 hover:bg-blue-500/5" : "border-slate-200 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/30"}`}
              >
                <svg className={`w-9 h-9 mb-2 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                <p className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Drop files or click to browse</p>
                <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}>PNG, JPG, PDF, DOC, XLSX, ZIP · up to 10MB each · max {MAX_FILES} files</p>
              </label>
            )}
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.xlsx,.txt,.zip"
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files);
                const remaining = MAX_FILES - attachments.length;
                if (remaining <= 0) { alert(`Maximum of ${MAX_FILES} files only!`); return; }
                const toAdd = picked.slice(0, remaining);
                if (picked.length > remaining) alert(`Only ${remaining} more file(s) allowed. Adding first ${remaining}.`);
                setAttachments(prev => [...prev, ...toAdd]);
                e.target.value = "";
              }}
            />

            {/* File chips */}
            {attachments.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {attachments.map((file, idx) => {
                  const isImg = file.type.startsWith("image/");
                  const isPdf = file.type.includes("pdf");
                  const icon = isPdf ? "📄" : file.type.includes("word") ? "📝" : file.type.includes("sheet") ? "📊" : file.type.includes("zip") ? "🗜️" : isImg ? "🖼️" : "📎";
                  const sizeStr = file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)}KB` : `${(file.size / (1024 * 1024)).toFixed(1)}MB`;
                  return (
                    <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all ${isDarkMode ? "bg-gray-800/60 border-gray-700/50" : "bg-slate-50 border-slate-200"}`}>
                      {isImg ? (
                        <img src={URL.createObjectURL(file)} alt={file.name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                      ) : (
                        <span className="text-lg shrink-0">{icon}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{file.name}</p>
                        <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{sizeStr}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="w-6 h-6 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/30 flex items-center justify-center text-xs transition-all shrink-0"
                      >✕</button>
                    </div>
                  );
                })}
                {attachments.length < MAX_FILES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center justify-center gap-2 w-full py-2 rounded-xl border-2 border-dashed text-xs font-semibold transition-all ${isDarkMode ? "border-gray-700 text-gray-500 hover:border-blue-500/50 hover:text-blue-400" : "border-slate-200 text-gray-400 hover:border-blue-400 hover:text-blue-500"}`}
                  >
                    + Add more ({MAX_FILES - attachments.length} remaining)
                  </button>
                )}
              </div>
            )}
          </FormField>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setCurrentView("dashboard")} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 text-gray-300" : "bg-slate-100 hover:bg-slate-200 text-gray-600"}`}>Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Creating...</> : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Submit Ticket</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

/* ─── MyTickets View ─────────────────────────────────────────────────────────── */
const MyTicketsView = ({ userData, setCurrentView, isDarkMode, onSelectTicket }) => {

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/Tickets`).then(r => r.json()).then(data => {
      setTickets(data.filter(t => t.email === userData.email)); setLoading(false);
    }).catch(() => setLoading(false));
  }, [userData.email]);

  const sortTickets = (list, method) => {
    const s = [...list];
    const p = { Critical: 4, High: 3, Medium: 2, Low: 1 };
    const st = { Open: 4, "In Progress": 3, Pending: 2, Resolved: 1, Closed: 0 };
    switch (method) {
      case "newest": return s.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case "oldest": return s.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case "priority": return s.sort((a, b) => (p[b.priority] || 0) - (p[a.priority] || 0));
      case "status": return s.sort((a, b) => (st[b.status] || 0) - (st[a.status] || 0));
      default: return s;
    }
  };

  const filtered = sortTickets(tickets.filter(t => {
    const ms = t.ticketNumber?.toLowerCase().includes(search.toLowerCase()) || t.subject?.toLowerCase().includes(search.toLowerCase());
    return ms && (filterStatus === "All" || t.status === filterStatus);
  }), sortBy);

  const statusStyle = s => ({
    Open: "bg-blue-500/15 text-blue-400 border-blue-500/30", Pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    "In Progress": "bg-indigo-500/15 text-indigo-400 border-indigo-500/30", Resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    Closed: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  }[s] || "bg-slate-500/15 text-slate-400 border-slate-500/30");

  const priStyle = p => ({
    Low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    High: "bg-orange-500/15 text-orange-400 border-orange-500/30", Critical: "bg-red-500/15 text-red-400 border-red-500/30",
  }[p] || "bg-slate-500/15 text-slate-400 border-slate-500/30");

  const formatDate = d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";
  const card = isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const sub = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = isDarkMode ? "bg-gray-800/80 border-gray-700/60 text-white placeholder-gray-500" : "bg-white border-slate-200 text-gray-900 placeholder-slate-400";

  const stats = [
    { label: "Total", val: tickets.length, color: "text-blue-400", bg: "from-blue-500/10" },
    { label: "Open", val: tickets.filter(t => t.status === "Open").length, color: "text-cyan-400", bg: "from-cyan-500/10" },
    { label: "In Progress", val: tickets.filter(t => t.status === "In Progress").length, color: "text-indigo-400", bg: "from-indigo-500/10" },
    { label: "Resolved", val: tickets.filter(t => t.status === "Resolved").length, color: "text-emerald-400", bg: "from-emerald-500/10" },
  ];

  return (
    <div className="animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => setCurrentView("dashboard")} className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all hover:-translate-x-0.5 ${isDarkMode ? "bg-gray-900/80 border-gray-800 text-gray-400 hover:text-white" : "bg-white border-slate-200 text-gray-500 hover:text-gray-900"} shadow-sm`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </button>
        <button onClick={() => setCurrentView("create-ticket")} className="flex items-center gap-2 px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-blue-500/30">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New Ticket
        </button>
      </div>

      <div className="mb-6">
        <h2 className={`font-display font-bold text-3xl ${text}`}>My Tickets</h2>
        <p className={`text-sm mt-1 ${sub}`}>Track all your support requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {stats.map(s => (
          <div key={s.label} className={`stat-card ${card} border rounded-2xl p-4 bg-gradient-to-br ${s.bg} to-transparent`}>
            <p className={`text-xs font-medium ${sub} mb-1 uppercase tracking-wider`}>{s.label}</p>
            <p className={`text-3xl font-display font-bold ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <div className="relative">
          <svg className={`absolute left-3.5 top-3 w-4 h-4 ${sub}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/40 transition-all ${inputCls}`} placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`px-4 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/40 transition-all ${inputCls}`}>
          {["All", "Open", "Pending", "In Progress", "Resolved", "Closed"].map(s => <option key={s} value={s}>{s === "All" ? "All Status" : s}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={`px-4 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/40 transition-all ${inputCls}`}>
          <option value="newest">Newest First</option><option value="oldest">Oldest First</option>
          <option value="priority">By Priority</option><option value="status">By Status</option>
        </select>
      </div>

      {loading && <div className="text-center py-16"><div className="inline-block w-10 h-10 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4" /><p className={`text-sm ${sub}`}>Loading tickets...</p></div>}

      {!loading && filtered.length === 0 && (
        <div className={`${card} border rounded-3xl text-center py-20`}>
          <div className={`w-20 h-20 rounded-2xl ${isDarkMode ? "bg-gray-800" : "bg-slate-100"} flex items-center justify-center mx-auto mb-5 animate-float`}>
            <svg className={`w-10 h-10 ${sub}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          </div>
          <h3 className={`font-display font-bold text-xl mb-2 ${text}`}>{search || filterStatus !== "All" ? "No matches found" : "No tickets yet"}</h3>
          <p className={`text-sm ${sub} mb-6`}>{search || filterStatus !== "All" ? "Try different filters." : "Submit your first support request."}</p>
          {!search && filterStatus === "All" && <button onClick={() => setCurrentView("create-ticket")} className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-sm transition-all shadow-lg">Create First Ticket</button>}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(ticket => (
            <div key={ticket.ticketNumber} onClick={() => onSelectTicket(ticket)}
              className={`group ${card} border rounded-2xl p-5 cursor-pointer transition-all hover:shadow-xl hover:border-blue-500/40 hover:-translate-y-1`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <p className={`font-mono text-xs ${sub} mb-1`}>{ticket.ticketNumber}</p>
                  <h3 className={`font-semibold truncate group-hover:text-blue-400 transition-colors ${text}`}>{ticket.subject || "Untitled"}</h3>
                </div>
                <svg className={`w-4 h-4 ${sub} ml-2 shrink-0 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </div>
              <p className={`text-sm ${sub} line-clamp-2 mb-4`}>{ticket.description || "No description"}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {[{ s: ticket.status, style: statusStyle }, { s: ticket.priority, style: priStyle }].map((b, i) => b.s && (
                  <span key={i} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${b.style(b.s)}`}>{b.s}</span>
                ))}
              </div>
              <div className={`flex items-center justify-between pt-3 border-t ${isDarkMode ? "border-gray-800" : "border-slate-100"}`}>
                <span className={`text-xs ${sub}`}>{formatDate(ticket.createdAt)}</span>
                {ticket.assignees && ticket.assignees !== "Unassigned" && <span className={`text-xs ${sub}`}>👤 {ticket.assignees}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && filtered.length > 0 && <p className={`text-center text-xs ${sub} mt-5`}>Showing {filtered.length} of {tickets.length} tickets</p>}


    </div>
  );
};

/* ─── Ticket Detail Modal ────────────────────────────────────────────────────── */
const REACTIONS = { "👍": "Like", "❤️": "Love", "😂": "Laugh", "👎": "Dislike" };
const REACTION_COLORS = { Like: "text-blue-400", Love: "text-red-400", Laugh: "text-yellow-400", Dislike: "text-slate-400" };
const API = config.API_BASE_URL;

// ─── Follow-Up Modal Component ───────────────────────────────────────────────
const FollowUpModal = ({ ticket, onClose, formatDate }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const listBottomRef = useRef(null);

  const fetchFollowUps = async () => {
    try {
      const res = await fetch(`${API}/TicketFollowUps/${encodeURIComponent(ticket.ticketNumber)}`);
      if (!res.ok) throw new Error();
      setFollowUps(await res.json());
    } catch {
      setFollowUps([]);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  useEffect(() => { fetchFollowUps(); }, [ticket.ticketNumber]);
  useEffect(() => { listBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [followUps.length]);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/TicketFollowUps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticketNumber: ticket.ticketNumber,
          message: message.trim(),
        }),
      });
      if (res.ok) {
        setMessage("");
        fetchFollowUps();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${API}/TicketFollowUps/${id}`, { method: "DELETE" });
      fetchFollowUps();
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = (followUp) => {
    setEditingId(followUp.followUpID);
    setEditText(followUp.message);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || !editingId) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/TicketFollowUps/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: editText.trim() }),
      });
      if (res.ok) {
        setEditingId(null);
        setEditText("");
        fetchFollowUps();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSubmit();
  };

  return (
    // Backdrop — stops clicks from bubbling to parent modal
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed", inset: 0, zIndex: 10999,
        backgroundColor: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%", maxWidth: "560px",
          background: "linear-gradient(160deg, #0f172a 0%, #1e293b 100%)",
          border: "1px solid rgba(148,163,184,0.15)",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
          maxHeight: "calc(100vh - 80px)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.9), 0 0 0 1px rgba(59,130,246,0.1)",
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)",
          padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid rgba(59,130,246,0.3)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {/* Bell icon */}
              <svg width="18" height="18" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <p style={{ color: "white", fontWeight: 700, fontSize: "15px", margin: 0 }}>
                Follow-Up Notes
              </p>
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", margin: 0 }}>
                {ticket.ticketNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: "32px", height: "32px", borderRadius: "50%",
              background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", transition: "all 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Follow-up List */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "16px 20px",
          display: "flex", flexDirection: "column", gap: "10px",
        }}>
          {loadingFollowUps && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", padding: "32px", color: "#64748b" }}>
              <div style={{
                width: "18px", height: "18px", borderRadius: "50%",
                border: "2px solid #3b82f6", borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "13px" }}>Loading follow-ups...</span>
            </div>
          )}

          {!loadingFollowUps && followUps.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: "52px", height: "52px", borderRadius: "14px",
                background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px",
              }}>
                <svg width="22" height="22" fill="none" stroke="#3b82f6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p style={{ color: "#64748b", fontSize: "13px", margin: "0 0 4px" }}>No follow-ups yet</p>
              <p style={{ color: "#334155", fontSize: "11px", margin: 0 }}>Add the first note below</p>
            </div>
          )}

          {!loadingFollowUps && followUps.map((f) => (
            <div key={f.followUpID} style={{
              background: "rgba(30,41,59,0.8)",
              border: "1px solid rgba(148,163,184,0.1)",
              borderRadius: "12px",
              padding: "12px 14px",
              transition: "border-color 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(59,130,246,0.3)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(148,163,184,0.1)"}
            >
              {editingId === f.followUpID ? (
                // Edit mode
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    autoFocus
                    rows={3}
                    style={{
                      width: "100%", background: "rgba(15,23,42,0.8)",
                      border: "1px solid rgba(59,130,246,0.4)", borderRadius: "8px",
                      padding: "8px 10px", color: "#e2e8f0", fontSize: "13px",
                      resize: "vertical", outline: "none", boxSizing: "border-box",
                      fontFamily: "inherit",
                    }}
                  />
                  <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                    <button
                      onClick={() => { setEditingId(null); setEditText(""); }}
                      style={{
                        padding: "5px 12px", borderRadius: "7px", fontSize: "11px",
                        background: "rgba(100,116,139,0.2)", border: "1px solid rgba(100,116,139,0.3)",
                        color: "#94a3b8", cursor: "pointer",
                      }}
                    >Cancel</button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      style={{
                        padding: "5px 12px", borderRadius: "7px", fontSize: "11px",
                        background: savingEdit ? "rgba(59,130,246,0.4)" : "#2563eb",
                        border: "none", color: "white", cursor: savingEdit ? "not-allowed" : "pointer",
                        fontWeight: 600,
                      }}
                    >{savingEdit ? "Saving..." : "Save"}</button>
                  </div>
                </div>
              ) : (
                // View mode
                <>
                  <p style={{ color: "#cbd5e1", fontSize: "13px", margin: "0 0 8px", lineHeight: "1.5" }}>
                    {f.message}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <svg width="11" height="11" fill="none" stroke="#475569" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span style={{ color: "#475569", fontSize: "11px" }}>
                        {formatDate(f.createdAt)}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {/* Edit button */}
                      <button
                        onClick={() => handleStartEdit(f)}
                        title="Edit"
                        style={{
                          width: "26px", height: "26px", borderRadius: "7px",
                          background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: "#60a5fa",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(59,130,246,0.25)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(59,130,246,0.1)"}
                      >
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {/* Delete button */}
                      <button
                        onClick={() => handleDelete(f.followUpID)}
                        disabled={deletingId === f.followUpID}
                        title="Delete"
                        style={{
                          width: "26px", height: "26px", borderRadius: "7px",
                          background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: deletingId === f.followUpID ? "not-allowed" : "pointer",
                          color: "#f87171",
                        }}
                        onMouseEnter={e => { if (deletingId !== f.followUpID) e.currentTarget.style.background = "rgba(239,68,68,0.25)"; }}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(239,68,68,0.1)"}
                      >
                        {deletingId === f.followUpID ? (
                          <div style={{
                            width: "10px", height: "10px", borderRadius: "50%",
                            border: "1.5px solid #f87171", borderTopColor: "transparent",
                            animation: "spin 0.8s linear infinite",
                          }} />
                        ) : (
                          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}

          <div ref={listBottomRef} />
        </div>

        {/* Compose area */}
        <div style={{
          padding: "14px 20px 20px",
          borderTop: "1px solid rgba(148,163,184,0.1)",
          background: "rgba(15,23,42,0.5)",
          flexShrink: 0,
        }}>
          <p style={{ color: "#475569", fontSize: "11px", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            New Follow-Up
          </p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your follow-up note here... (Ctrl+Enter to submit)"
            rows={3}
            style={{
              width: "100%", background: "rgba(30,41,59,0.9)",
              border: "1px solid rgba(148,163,184,0.15)", borderRadius: "10px",
              padding: "10px 12px", color: "#e2e8f0", fontSize: "13px",
              resize: "none", outline: "none", boxSizing: "border-box",
              fontFamily: "inherit", lineHeight: "1.5",
              transition: "border-color 0.2s",
            }}
            onFocus={e => e.target.style.borderColor = "rgba(59,130,246,0.5)"}
            onBlur={e => e.target.style.borderColor = "rgba(148,163,184,0.15)"}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
            <span style={{ color: "#334155", fontSize: "11px" }}>
              {message.length > 0 ? `${message.length} chars` : ""}
            </span>
            <button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              style={{
                display: "flex", alignItems: "center", gap: "7px",
                padding: "8px 18px", borderRadius: "9px",
                background: submitting || !message.trim()
                  ? "rgba(59,130,246,0.3)"
                  : "linear-gradient(135deg, #2563eb, #3b82f6)",
                border: "none", color: "white", fontSize: "13px", fontWeight: 600,
                cursor: submitting || !message.trim() ? "not-allowed" : "pointer",
                transition: "all 0.2s", boxShadow: message.trim() && !submitting
                  ? "0 4px 12px rgba(37,99,235,0.4)" : "none",
              }}
            >
              {submitting ? (
                <>
                  <div style={{
                    width: "12px", height: "12px", borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  Submitting...
                </>
              ) : (
                <>
                  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Follow-Up
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Spin keyframes */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Main TicketDetailModal (updated with follow-up trigger) ─────────────────
const TicketDetailModal = ({ ticket, onClose, getStatusStyle, getPriorityStyle, formatDate, formatDateTime }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [postingReply, setPostingReply] = useState(false);
  const feedBottomRef = useRef(null);
  const [currentUserPic, setCurrentUserPic] = useState(null);
  const [assigneePic, setAssigneePic] = useState(null);

  // ── Follow-up modal state ──
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpCount, setFollowUpCount] = useState(0);

  useEffect(() => {
    fetch(`${API}/TicketFollowUps/${encodeURIComponent(ticket.ticketNumber)}/count`)
      .then(r => r.json())
      .then(data => setFollowUpCount(data.count ?? 0))
      .catch(() => { });
  }, [ticket.ticketNumber]);

  useEffect(() => {
    if (!ticket.assignees || ticket.assignees === "Unassigned") return;
    fetch(`${API}/Admin`).then(r => r.json()).then(admins => {
      const match = admins.find(a => a.fullNames === ticket.assignees || a.username === ticket.assignees);
      if (!match) return;
      fetch(`${API}/Admin/get-profile-picture/${match.username}`).then(r => r.ok ? r.blob() : null).then(blob => { if (blob) setAssigneePic(URL.createObjectURL(blob)); }).catch(() => { });
    }).catch(() => { });
  }, [ticket.assignees]);

  const currentUser = (() => {
    try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return { userId: u.username || "user", userName: u.fullNames || u.username || "User" }; }
    catch { return { userId: "user", userName: "User" }; }
  })();

  const steps = ["Submitted", "In Review", "Resolved"];
  const stepIndex = { Open: 0, Pending: 0, "In Progress": 1, Resolved: 2, Closed: 2 }[ticket.status] ?? 0;
  const progressPct = stepIndex === 0 ? 10 : stepIndex === 1 ? 55 : 100;
  const initials = name => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  useEffect(() => {
    fetch(`${API}/Users/get-profile-picture/${currentUser.userId}`).then(r => r.ok ? r.blob() : null).then(blob => { if (blob) setCurrentUserPic(URL.createObjectURL(blob)); }).catch(() => { });
  }, [currentUser.userId]);

  const fetchActivities = async () => {
    try {
      const res = await fetch(`${API}/Activities/ticket/${encodeURIComponent(ticket.ticketNumber)}?userId=${currentUser.userId}`);
      if (!res.ok) throw new Error();
      setActivities(await res.json());
    } catch { setActivities([]); } finally { setLoadingActivities(false); }
  };

  useEffect(() => { fetchActivities(); }, [ticket.ticketNumber]);
  useEffect(() => { feedBottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [activities.length]);

  const handlePostComment = async (activityId) => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      const res = await fetch(`${API}/Activities/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ activityId, userId: currentUser.userId, userName: currentUser.userName, content: commentText.trim() }) });
      if (res.ok) { setCommentText(""); fetchActivities(); }
    } catch (e) { console.error(e); } finally { setPostingComment(false); }
  };

  const handlePostReply = async () => {
    if (!replyText.trim() || !replyingTo) return;
    setPostingReply(true);
    try {
      const res = await fetch(`${API}/Activities/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ activityId: replyingTo.activityId, parentCommentId: replyingTo.commentId, userId: currentUser.userId, userName: currentUser.userName, content: replyText.trim() }) });
      if (res.ok) { setReplyText(""); setReplyingTo(null); fetchActivities(); }
    } catch (e) { console.error(e); } finally { setPostingReply(false); }
  };

  const handleActivityReaction = async (activityId, reactionType) => {
    const activity = activities.find(a => a.activityId === activityId);
    const existing = activity?.reactionSummary?.userReaction;
    try {
      if (existing === reactionType) { await fetch(`${API}/Activities/${activityId}/reactions/${currentUser.userId}`, { method: "DELETE" }); }
      else { await fetch(`${API}/Activities/${activityId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.userId, userName: currentUser.userName, reactionType }) }); }
      fetchActivities();
    } catch (e) { console.error(e); }
  };

  const handleCommentReaction = async (commentId, reactionType) => {
    let existing = null;
    for (const act of activities) {
      const c = act.comments?.find(x => x.commentId === commentId);
      if (c) { existing = c.reactionSummary?.userReaction; break; }
      for (const c2 of act.comments || []) { const r = c2.replies?.find(x => x.commentId === commentId); if (r) { existing = r.reactionSummary?.userReaction; break; } }
    }
    try {
      if (existing === reactionType) { await fetch(`${API}/Activities/comments/${commentId}/reactions/${currentUser.userId}`, { method: "DELETE" }); }
      else { await fetch(`${API}/Activities/comments/${commentId}/reactions`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userId: currentUser.userId, userName: currentUser.userName, reactionType }) }); }
      fetchActivities();
    } catch (e) { console.error(e); }
  };

  return (
    <>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: "32px", left: "50%", transform: "translateX(-50%)", width: "calc(100% - 64px)", maxWidth: "1400px", height: "calc(100vh - 64px)", zIndex: 9999, background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(148,163,184,0.2)", borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.9)" }}>
          <div className="flex items-center justify-between px-8 py-6 shrink-0" style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 60%, #3b82f6 100%)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg></div>
              <div><h2 className="font-display font-bold text-white text-lg">{ticket.ticketNumber}</h2><p className="text-white/60 text-xs">{formatDate(ticket.createdAt)}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(ticket.status)}`}>{ticket.status}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityStyle(ticket.priority)}`}>{ticket.priority}</span>
              <button onClick={onClose} className="ml-2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-all hover:rotate-90"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar */}
            <div className="w-72 shrink-0 border-r border-slate-700/40 overflow-y-auto p-5 space-y-5" style={{ backgroundColor: "#1e293b" }}>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Details</p>
                <div className="space-y-3">
                  <SidebarItem label="Subject" value={ticket.subject} />
                  <SidebarItem label="Ticket ID" value={ticket.ticketNumber} mono />
                  <SidebarItem label="Category" value={ticket.category} />
                  <div><p className="text-xs text-slate-500 mb-1">Priority</p><span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityStyle(ticket.priority)}`}>{ticket.priority || "N/A"}</span></div>
                  <div><p className="text-xs text-slate-500 mb-1">Status</p><span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(ticket.status)}`}>{ticket.status || "N/A"}</span></div>
                  <SidebarItem label="Submitted" value={formatDate(ticket.createdAt)} />
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Submitted By</p>
                <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-700/40 border border-slate-600/30 mb-2">
                  <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold shrink-0">{initials(ticket.name)}</div>
                  <div className="min-w-0"><p className="text-sm font-semibold text-white truncate">{ticket.name || "—"}</p><p className="text-xs text-slate-400 truncate">{ticket.email}</p></div>
                </div>
              </div>
            </div>

            {/* Center feed */}
            <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: "#0f172a" }}>
              <div className="px-6 py-4 border-b border-slate-700/30 shrink-0" style={{ backgroundColor: "#1e293b" }}>
                <div className="flex items-center justify-between mb-2"><p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Progress</p><p className="text-sm font-bold text-blue-400">{progressPct}%</p></div>
                <div className="w-full bg-slate-700/40 rounded-full h-1.5 mb-4"><div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg,#3b82f6,#06b6d4)" }} /></div>
                <div className="flex items-center">
                  {steps.map((step, i) => (
                    <React.Fragment key={step}>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all ${i <= stepIndex ? "bg-blue-500 border-blue-500" : "bg-transparent border-slate-600"}`}>{i <= stepIndex && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}</div>
                        <span className={`text-xs font-medium ${i <= stepIndex ? "text-blue-400" : "text-slate-500"}`}>{step}</span>
                      </div>
                      {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${i < stepIndex ? "bg-blue-500" : "bg-slate-700"}`} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Activity Feed</p>
                {loadingActivities && <div className="flex items-center gap-3 text-slate-500 text-sm py-8 justify-center"><div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />Loading activity...</div>}
                {!loadingActivities && activities.length === 0 && !ticket.description && <div className="text-center py-10"><p className="text-slate-500 text-sm">No activity yet</p><p className="text-slate-600 text-xs mt-1">Updates from support will appear here</p></div>}
                {!loadingActivities && activities.map(activity => (
                  <ActivityCard key={activity.activityId} activity={activity} currentUser={currentUser} currentUserPic={currentUserPic} onReact={type => handleActivityReaction(activity.activityId, type)} onCommentReact={handleCommentReaction} onPostComment={() => handlePostComment(activity.activityId)} commentText={commentText} setCommentText={setCommentText} postingComment={postingComment} replyingTo={replyingTo} setReplyingTo={setReplyingTo} replyText={replyText} setReplyText={setReplyText} onPostReply={handlePostReply} postingReply={postingReply} formatDate={formatDate} initials={initials} apiBase={API} />
                ))}
                {!loadingActivities && ticket.description && (
                  <div className="rounded-xl border border-slate-700/30 p-4" style={{ backgroundColor: "#1a2235" }}>
                    <div className="flex items-center gap-2 mb-2"><svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg><p className="text-xs text-slate-500 font-medium">Original Request</p></div>
                    <p className="text-sm text-slate-400 leading-relaxed">{ticket.description}</p>
                  </div>
                )}
                <div ref={feedBottomRef} />
              </div>
              <div className="px-6 py-3 border-t border-slate-700/30 shrink-0"><p className="text-xs text-slate-600 text-center">💬 React or comment on each activity above</p></div>
            </div>

            {/* Right panel */}
            <div className="w-64 shrink-0 border-l border-slate-700/40 overflow-y-auto p-5 space-y-5" style={{ backgroundColor: "#1e293b" }}>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Assigned To</p>
                {ticket.assignees && ticket.assignees !== "" && ticket.assignees !== "Unassigned" ? (
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-indigo-500/80 flex items-center justify-center text-white text-xs font-bold shrink-0 overflow-hidden">{assigneePic ? <img src={assigneePic} className="w-full h-full object-cover" alt={ticket.assignees} /> : initials(ticket.assignees)}</div>
                    <div className="min-w-0"><p className="text-sm font-semibold text-white truncate">{ticket.assignees}</p><p className="text-xs text-slate-500">Assignee</p></div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5"><div className="w-9 h-9 rounded-full bg-slate-600/50 border border-slate-600 flex items-center justify-center"><svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg></div><p className="text-sm text-slate-500 italic">Unassigned</p></div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Quick Stats</p>
                <div className="space-y-3">
                  {[
                    { label: "Days Open", val: ticket.createdAt ? Math.floor((Date.now() - new Date(ticket.createdAt)) / 86400000) : "—" },
                    { label: "Priority", val: ticket.priority || "—", color: { Low: "text-emerald-400", Medium: "text-yellow-400", High: "text-orange-400", Critical: "text-red-400" }[ticket.priority] || "text-slate-400" },
                    { label: "Stage", val: ticket.status || "—", color: { Resolved: "text-emerald-400", Closed: "text-emerald-400", "In Progress": "text-blue-400", Pending: "text-yellow-400" }[ticket.status] || "text-slate-400" },
                  ].map((item, i) => (
                    <React.Fragment key={i}>
                      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{item.label}</span><span className={`text-xs font-semibold ${item.color || "text-slate-300"}`}>{item.val}</span></div>
                      {i < 2 && <div className="w-full h-px bg-slate-700/50" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Labels</p>
                <div className="flex flex-wrap gap-1.5">
                  {ticket.category && <span className="px-2 py-1 rounded-full text-xs bg-blue-500/15 text-blue-400 border border-blue-500/25">{ticket.category}</span>}
                  {ticket.priority && <span className={`px-2 py-1 rounded-full text-xs border ${{ Low: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", Medium: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25", High: "bg-orange-500/15 text-orange-400 border-orange-500/25", Critical: "bg-red-500/15 text-red-400 border-red-500/25" }[ticket.priority] || "bg-slate-500/15 text-slate-400 border-slate-500/25"}`}>{ticket.priority}</span>}
                </div>
              </div>

              {/* ── Follow-Up Button ── */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Follow-Up</p>
                <button
                  onClick={() => setShowFollowUpModal(true)}
                  style={{
                    width: "100%",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: "10px",
                    background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(59,130,246,0.08))",
                    border: "1px solid rgba(59,130,246,0.25)",
                    cursor: "pointer", transition: "all 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(37,99,235,0.3), rgba(59,130,246,0.18))";
                    e.currentTarget.style.borderColor = "rgba(59,130,246,0.5)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(37,99,235,0.2)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(59,130,246,0.08))";
                    e.currentTarget.style.borderColor = "rgba(59,130,246,0.25)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <svg width="15" height="15" fill="none" stroke="#60a5fa" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span style={{ color: "#93c5fd", fontSize: "12px", fontWeight: 600 }}>View Follow-Ups</span>
                  </div>
                  {followUpCount > 0 && (
                    <span style={{
                      background: "#2563eb", color: "white",
                      fontSize: "10px", fontWeight: 700,
                      padding: "2px 7px", borderRadius: "999px",
                      minWidth: "18px", textAlign: "center",
                    }}>{followUpCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Attachments Bar */}
          <div className="shrink-0 border-t border-slate-700/40 px-6 py-4" style={{ backgroundColor: "#1a2744" }}>
            <AttachmentsSection ticketNumber={ticket.ticketNumber} horizontal />
          </div>
        </div>
      </div>

      {/* ── Follow-Up Modal (renders on top) ── */}
      {showFollowUpModal && (
        <FollowUpModal
          ticket={ticket}
          onClose={() => {
            setShowFollowUpModal(false);
            // Refresh count after closing
            fetch(`${API}/TicketFollowUps/${encodeURIComponent(ticket.ticketNumber)}/count`)
              .then(r => r.json())
              .then(data => setFollowUpCount(data.count ?? 0))
              .catch(() => { });
          }}
          formatDate={formatDate}
        />
      )}
    </>
  );
};


function ActivityCard({ activity, currentUser, onReact, onCommentReact, onPostComment, commentText, setCommentText, postingComment, replyingTo, setReplyingTo, replyText, setReplyText, onPostReply, currentUserPic, postingReply, formatDate, initials, apiBase }) {
  const [showComments, setShowComments] = useState(true);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [activityUserPic, setActivityUserPic] = useState(null);
  const pickerRef = useRef(null);
  useEffect(() => {
    if (!activity.userId) return;
    fetch(`${apiBase}/Admin/get-profile-picture/${activity.userId}`).then(r => r.ok ? r.blob() : null).then(blob => { if (blob) setActivityUserPic(URL.createObjectURL(blob)); }).catch(() => { });
  }, [activity.userId, apiBase]);
  useEffect(() => {
    const h = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowReactionPicker(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const myReaction = activity.reactionSummary?.userReaction;
  const totalReactions = activity.reactionSummary?.totalReactions || 0;
  const reactionCounts = activity.reactionSummary?.reactionCounts || {};
  return (
    <div className="rounded-xl border border-slate-700/40 overflow-hidden" style={{ backgroundColor: "#1e293b" }}>
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold shrink-0 overflow-hidden">{activityUserPic ? <img src={activityUserPic} alt={activity.userName} className="w-full h-full object-cover" /> : initials(activity.userName || "S")}</div>
        <div><p className="text-sm font-semibold text-white leading-tight">{activity.userName || "Support"}</p><p className="text-xs text-slate-500">{formatDate ? formatDate(activity.createdAt) : activity.createdAt}</p></div>
      </div>
      {activity.content && activity.content.trim() && <p className="px-4 pb-2 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{activity.content}</p>}
      {activity.hasImage && <div className="px-4 pb-3"><img src={`${apiBase}/Activities/${activity.activityId}/image`} alt="Activity" className="max-h-56 rounded-lg border border-slate-700 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(`${apiBase}/Activities/${activity.activityId}/image`, "_blank")} /></div>}
      {totalReactions > 0 && <div className="px-4 pb-2 flex items-center gap-1.5"><div className="flex -space-x-1">{Object.entries(reactionCounts).map(([type, count]) => count > 0 ? <span key={type} className="text-xs">{Object.entries(REACTIONS).find(([, v]) => v === type)?.[0]}</span> : null)}</div><span className="text-xs text-slate-400">{totalReactions}</span></div>}
      <div className="flex items-center gap-1 px-3 py-2 border-t border-slate-700/30">
        <div className="relative" ref={pickerRef}>
          <button onClick={() => setShowReactionPicker(p => !p)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-slate-700/60 ${myReaction ? REACTION_COLORS[myReaction] : "text-slate-400 hover:text-slate-200"}`}>{myReaction ? <>{Object.entries(REACTIONS).find(([, v]) => v === myReaction)?.[0]} {myReaction}</> : <>👍 React</>}</button>
          {showReactionPicker && <div className="absolute bottom-full left-0 mb-2 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-2 flex gap-1 z-20">{Object.entries(REACTIONS).map(([emoji, type]) => <button key={type} onClick={() => { onReact(type); setShowReactionPicker(false); }} title={type} className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg transition-all hover:bg-slate-700 hover:scale-125 ${myReaction === type ? "bg-slate-700 ring-2 ring-blue-500" : ""}`}>{emoji}</button>)}</div>}
        </div>
        <button onClick={() => { setShowCommentBox(p => !p); setShowComments(true); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 transition-all">💬 {activity.commentCount > 0 ? `${activity.commentCount} Comment${activity.commentCount > 1 ? "s" : ""}` : "Comment"}</button>
      </div>
      {showCommentBox && <div className="px-4 pb-3 border-t border-slate-700/20" style={{ backgroundColor: "#162032" }}><div className="flex items-start gap-2 pt-3"><div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5 overflow-hidden">{currentUserPic ? <img src={currentUserPic} alt="me" className="w-full h-full object-cover" /> : currentUser.userName.charAt(0).toUpperCase()}</div><div className="flex-1"><textarea value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onPostComment(); } }} placeholder="Write a comment..." rows={2} className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/60 resize-none transition-all" /><div className="flex justify-end gap-2 mt-1.5"><button onClick={() => { setShowCommentBox(false); setCommentText(""); }} className="px-3 py-1 text-xs text-slate-400 hover:text-white rounded-lg transition-colors">Cancel</button><button onClick={onPostComment} disabled={!commentText.trim() || postingComment} className="px-4 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5">{postingComment ? <><div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />Posting...</> : "Post"}</button></div></div></div></div>}
      {showComments && activity.comments?.length > 0 && <div className="px-4 pb-3 space-y-2 border-t border-slate-700/20" style={{ backgroundColor: "#162032" }}>{activity.comments.map(comment => <CommentItem key={comment.commentId} comment={comment} activityId={activity.activityId} currentUser={currentUser} onReact={onCommentReact} replyingTo={replyingTo} setReplyingTo={setReplyingTo} replyText={replyText} setReplyText={setReplyText} onPostReply={onPostReply} postingReply={postingReply} formatDate={formatDate} initials={initials} />)}</div>}
    </div>
  );
}

function CommentItem({ comment, activityId, currentUser, onReact, replyingTo, setReplyingTo, replyText, setReplyText, onPostReply, postingReply, formatDate, initials }) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const myReaction = comment.reactionSummary?.userReaction;
  const isReplying = replyingTo?.commentId === comment.commentId;
  useEffect(() => { const h = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div className="mt-2">
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5">{initials(comment.userName)}</div>
        <div className="flex-1 min-w-0">
          <div className="bg-slate-800/60 rounded-xl px-3 py-2 border border-slate-700/30"><p className="text-xs font-semibold text-white mb-0.5">{comment.userName}</p><p className="text-xs text-slate-300 leading-relaxed">{comment.content}</p></div>
          <div className="flex items-center gap-3 mt-1 ml-1">
            <span className="text-xs text-slate-600">{formatDate ? formatDate(comment.createdAt) : ""}</span>
            <div className="relative" ref={pickerRef}>
              <button onClick={() => setShowPicker(p => !p)} className={`text-xs transition-colors ${myReaction ? REACTION_COLORS[myReaction] : "text-slate-500 hover:text-slate-300"}`}>{myReaction ? `${Object.entries(REACTIONS).find(([, v]) => v === myReaction)?.[0]} ${myReaction}` : "React"}</button>
              {showPicker && <div className="absolute bottom-full left-0 mb-1 bg-slate-800 border border-slate-700 rounded-xl shadow-xl p-1.5 flex gap-1 z-20">{Object.entries(REACTIONS).map(([emoji, type]) => <button key={type} onClick={() => { onReact(comment.commentId, type); setShowPicker(false); }} className={`w-8 h-8 rounded-lg flex items-center justify-center text-base transition-all hover:bg-slate-700 hover:scale-125 ${myReaction === type ? "bg-slate-700 ring-2 ring-blue-500" : ""}`} title={type}>{emoji}</button>)}</div>}
            </div>
            <button onClick={() => setReplyingTo(isReplying ? null : { activityId, commentId: comment.commentId, userName: comment.userName })} className="text-xs text-slate-500 hover:text-blue-400 transition-colors">{isReplying ? "Cancel" : "Reply"}</button>
            {comment.reactionSummary?.totalReactions > 0 && <span className="text-xs text-slate-500">{comment.reactionSummary.totalReactions} reaction{comment.reactionSummary.totalReactions > 1 ? "s" : ""}</span>}
          </div>
          {isReplying && <div className="mt-2 ml-2 flex items-start gap-2"><div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5">{currentUser.userName.charAt(0).toUpperCase()}</div><div className="flex-1"><textarea value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onPostReply(); } }} placeholder={`Reply to ${replyingTo.userName}...`} rows={2} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/60 resize-none" /><div className="flex justify-end gap-2 mt-1"><button onClick={() => { setReplyingTo(null); setReplyText(""); }} className="text-xs text-slate-400 hover:text-white px-2 py-0.5 rounded transition-colors">Cancel</button><button onClick={onPostReply} disabled={!replyText.trim() || postingReply} className="px-3 py-0.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-all">{postingReply ? "..." : "Reply"}</button></div></div></div>}
          {comment.replies?.length > 0 && <div className="mt-2 ml-3 space-y-2 border-l-2 border-slate-700/40 pl-3">{comment.replies.map(reply => <div key={reply.commentId} className="flex items-start gap-2"><div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-blue-400 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5">{initials(reply.userName)}</div><div className="flex-1"><div className="bg-slate-800/40 rounded-xl px-3 py-1.5 border border-slate-700/20"><p className="text-xs font-semibold text-white mb-0.5">{reply.userName}</p><p className="text-xs text-slate-300">{reply.content}</p></div></div></div>)}</div>}
        </div>
      </div>
    </div>
  );
}

/* ─── Attachments Section Component ─────────────────────────────────────────── */
const AttachmentsSection = ({ ticketNumber, horizontal = false }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);
  const MAX_FILES = 6;

  const API_ATT = `${config.API_BASE_URL}/TicketAttachments`;

  const loadAttachments = async () => {
    try {
      const res = await fetch(`${API_ATT}/${ticketNumber}`);
      if (res.ok) setAttachments(await res.json());
    } catch { }
  };

  useEffect(() => { loadAttachments(); }, [ticketNumber]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remaining = MAX_FILES - attachments.length;
    if (remaining <= 0) {
      alert(`Maximum of ${MAX_FILES} files only!`); return;
    }

    const toUpload = files.slice(0, remaining);
    if (files.length > remaining)
      alert(`Only ${remaining} more file(s) allowed. Uploading first ${remaining}.`);

    setUploading(true);
    const formData = new FormData();
    toUpload.forEach(f => formData.append("files", f));

    try {
      const res = await fetch(`${API_ATT}/${ticketNumber}/upload`, {
        method: "POST", body: formData,
      });
      if (res.ok) loadAttachments();
    } catch { } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (id, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    try {
      await fetch(`${API_ATT}/file/${id}`, { method: "DELETE" });
      loadAttachments();
    } catch { }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const isImage = (ct) => ct?.startsWith("image/");
  const isPDF = (ct) => ct?.includes("pdf");

  const getFileIcon = (contentType) => {
    if (isImage(contentType)) return null;
    if (isPDF(contentType)) return "📄";
    if (contentType?.includes("word") || contentType?.includes("docx")) return "📝";
    if (contentType?.includes("sheet") || contentType?.includes("xlsx")) return "📊";
    if (contentType?.includes("zip")) return "🗜️";
    return "📎";
  };

  if (horizontal) {
    return (
      <>
        {previewFile && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }} onClick={() => setPreviewFile(null)}>
            <div className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between w-full mb-3 px-2">
                <p className="text-white text-sm font-medium truncate">{previewFile.fileName}</p>
                <div className="flex items-center gap-2">
                  <a href={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`} download={previewFile.fileName} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-all">⬇ Download</a>
                  <button onClick={() => setPreviewFile(null)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white text-lg transition-all">✕</button>
                </div>
              </div>
              {isImage(previewFile.contentType) ? (
                <img src={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`} alt={previewFile.fileName} className="max-h-[80vh] max-w-full rounded-xl object-contain shadow-2xl" />
              ) : isPDF(previewFile.contentType) ? (
                <iframe src={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`} className="w-full h-[80vh] rounded-xl border border-slate-700" title={previewFile.fileName} />
              ) : null}
            </div>
          </div>
        )}
        <div className="flex items-center gap-4">
          {/* Label + Add button */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">📎 Attachments</span>
            {attachments.length > 0 && (
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full font-bold">{attachments.length}/{MAX_FILES}</span>
            )}
            {attachments.length < MAX_FILES && (
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-1 px-2.5 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/25 transition-all text-xs font-semibold disabled:opacity-50">
                {uploading ? <><div className="w-3 h-3 border border-blue-400/40 border-t-blue-400 rounded-full animate-spin" /> Uploading...</> : <>+ Add</>}
              </button>
            )}
            <input ref={fileInputRef} type="file" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.xlsx,.txt,.zip" onChange={handleUpload} className="hidden" />
          </div>

          {/* Horizontal scrollable file strip */}
          {attachments.length === 0 ? (
            <div onClick={() => fileInputRef.current?.click()} className="flex-1 border border-dashed border-slate-700 rounded-xl px-4 py-2 text-center cursor-pointer hover:border-blue-500/50 hover:bg-blue-500/5 transition-all">
              <p className="text-slate-600 text-xs">Click to upload files · Max {MAX_FILES} · 10MB each</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 overflow-x-auto flex-1 pb-1" style={{ scrollbarWidth: "thin" }}>
              {attachments.map(att => {
                const img = isImage(att.contentType);
                const pdf = isPDF(att.contentType);
                const icon = pdf ? "📄" : att.contentType?.includes("word") ? "📝" : att.contentType?.includes("sheet") ? "📊" : att.contentType?.includes("zip") ? "🗜️" : "📎";
                const sizeStr = att.fileSize < 1024 * 1024 ? `${(att.fileSize / 1024).toFixed(1)}KB` : `${(att.fileSize / (1024 * 1024)).toFixed(1)}MB`;
                return (
                  <div key={att.id} onClick={() => (img || pdf) && setPreviewFile(att)} className={`group relative flex flex-col items-center gap-1 p-2 rounded-xl border border-slate-700/50 bg-slate-800/60 hover:border-blue-500/40 transition-all shrink-0 ${(img || pdf) ? "cursor-pointer" : ""}`} style={{ minWidth: "80px", maxWidth: "90px" }}>
                    {img ? (
                      <img src={`${config.API_BASE_URL}/TicketAttachments/file/${att.id}`} alt={att.fileName} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-700/60 flex items-center justify-center text-2xl">{icon}</div>
                    )}
                    <p className="text-xs text-slate-300 truncate w-full text-center leading-tight">{att.fileName.length > 10 ? att.fileName.slice(0, 10) + "…" : att.fileName}</p>
                    <p className="text-xs text-slate-500">{sizeStr}</p>
                    {/* Hover actions */}
                    <div className="absolute inset-0 rounded-xl bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-1">
                      {(img || pdf) && <button onClick={(e) => { e.stopPropagation(); setPreviewFile(att); }} className="w-7 h-7 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white text-xs">👁</button>}
                      <a href={`${config.API_BASE_URL}/TicketAttachments/file/${att.id}`} download={att.fileName} onClick={e => e.stopPropagation()} className="w-7 h-7 bg-blue-500/70 hover:bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">⬇</a>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(att.id, att.fileName); }} className="w-7 h-7 bg-red-500/70 hover:bg-red-500 rounded-full flex items-center justify-center text-white text-xs">✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {/* File Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.92)", backdropFilter: "blur(8px)" }}
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between w-full mb-3 px-2">
              <p className="text-white text-sm font-medium truncate">{previewFile.fileName}</p>
              <div className="flex items-center gap-2">
                <a
                  href={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`}
                  download={previewFile.fileName}
                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-all"
                >
                  ⬇ Download
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white text-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>
            {isImage(previewFile.contentType) ? (
              <img
                src={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`}
                alt={previewFile.fileName}
                className="max-h-[80vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
            ) : isPDF(previewFile.contentType) ? (
              <iframe
                src={`${config.API_BASE_URL}/TicketAttachments/file/${previewFile.id}`}
                className="w-full h-[80vh] rounded-xl border border-slate-700"
                title={previewFile.fileName}
              />
            ) : null}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
            📎 Attachments
            {attachments.length > 0 && (
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full font-bold">
                {attachments.length}/{MAX_FILES}
              </span>
            )}
          </p>
          {attachments.length < MAX_FILES && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1 px-2.5 py-1 bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/25 transition-all text-xs font-semibold disabled:opacity-50"
            >
              {uploading ? (
                <><div className="w-3 h-3 border border-blue-400/40 border-t-blue-400 rounded-full animate-spin" /> Uploading...</>
              ) : (
                <>+ Add</>
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.xlsx,.txt,.zip"
            onChange={handleUpload}
            className="hidden"
          />
        </div>

        {attachments.length === 0 ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border border-dashed border-slate-700 rounded-xl p-4 text-center cursor-pointer hover:border-blue-500/50 hover:bg-blue-500/5 transition-all"
          >
            <p className="text-slate-600 text-xs">Click to upload files</p>
            <p className="text-slate-700 text-xs mt-0.5">Max {MAX_FILES} files · 10MB each</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Image thumbnails grid */}
            {attachments.some(a => isImage(a.contentType)) && (
              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {attachments.filter(a => isImage(a.contentType)).map(att => (
                  <div key={att.id} className="relative group rounded-lg overflow-hidden aspect-square border border-slate-700/50">
                    <img
                      src={`${config.API_BASE_URL}/TicketAttachments/file/${att.id}`}
                      alt={att.fileName}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setPreviewFile(att)}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => setPreviewFile(att)}
                        className="w-7 h-7 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white text-xs transition-all"
                      >
                        👁
                      </button>
                      <button
                        onClick={() => handleDelete(att.id, att.fileName)}
                        className="w-7 h-7 bg-red-500/70 hover:bg-red-500 rounded-full flex items-center justify-center text-white text-xs transition-all"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-all">
                      <p className="text-white text-xs truncate">{formatSize(att.fileSize)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Non-image file list */}
            {attachments.filter(a => !isImage(a.contentType)).map(att => (
              <div key={att.id} className="flex items-center gap-2.5 p-2 rounded-lg border border-slate-700/40 bg-slate-800/40 group hover:border-blue-500/30 hover:bg-slate-800/60 transition-all">
                <span className="text-lg shrink-0">{getFileIcon(att.contentType)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-200 truncate font-medium">{att.fileName}</p>
                  <p className="text-xs text-slate-500">{formatSize(att.fileSize)}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isPDF(att.contentType) && (
                    <button
                      onClick={() => setPreviewFile(att)}
                      className="w-6 h-6 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 flex items-center justify-center text-xs transition-all"
                      title="Preview"
                    >
                      👁
                    </button>
                  )}
                  <a
                    href={`${config.API_BASE_URL}/TicketAttachments/file/${att.id}`}
                    download={att.fileName}
                    className="w-6 h-6 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center justify-center text-xs transition-all"
                    title="Download"
                  >
                    ⬇
                  </a>
                  <button
                    onClick={() => handleDelete(att.id, att.fileName)}
                    className="w-6 h-6 rounded bg-red-500/20 text-red-400 hover:bg-red-500/40 flex items-center justify-center text-xs transition-all"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const SidebarItem = ({ label, value, mono }) => (
  <div><p className="text-xs text-slate-500 mb-0.5">{label}</p><p className={`text-sm font-medium text-slate-200 ${mono ? "font-mono text-xs break-all" : ""}`}>{value || "N/A"}</p></div>
);

/* ─── Profile View ───────────────────────────────────────────────────────────── */
const ProfileView = ({ userData, profilePicture, loadProfilePicture, setCurrentView, isDarkMode }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) { setPasswordMessage("Please fill out both fields."); return; }
    if (newPassword !== confirmPassword) { setPasswordMessage("Passwords do not match."); return; }
    setPasswordLoading(true); setPasswordMessage("");
    try {
      const res = await fetch(`http://localhost:2024/api/Users/update-password/${userData.username}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ newPassword, confirmPassword }) });
      if (res.ok) { setPasswordMessage("Password updated successfully!"); setNewPassword(""); setConfirmPassword(""); }
      else { const d = await res.json(); setPasswordMessage(d.message || "Failed to update password."); }
    } catch { setPasswordMessage("Error updating password."); } finally { setPasswordLoading(false); }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await fetch(`${config.API_BASE_URL}/Users/upload-profile-picture?username=${userData.username}`, { method: "POST", body: fd });
      if (res.ok) { setUploadSuccess(true); setTimeout(() => setUploadSuccess(false), 3000); loadProfilePicture(userData.username); }
    } catch { alert("Failed to upload profile picture"); } finally { setUploading(false); }
  };

  const card = isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const inputCls = isDarkMode ? "bg-gray-800/80 border-gray-700/60 text-white placeholder-gray-500 focus:border-blue-500" : "bg-white border-slate-200 text-gray-900 placeholder-slate-400 focus:border-blue-500";

  return (
    <div className="max-w-3xl mx-auto animate-slide-up">
      <button onClick={() => setCurrentView("dashboard")} className={`mb-6 flex items-center gap-2 text-sm font-medium transition-all hover:-translate-x-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Dashboard
      </button>

      {/* Tabs */}
      <div className={`inline-flex gap-1 p-1 rounded-2xl mb-6 ${isDarkMode ? "bg-gray-800/60 border border-gray-700/40" : "bg-slate-100 border border-slate-200/60"}`}>
        {[{ id: "profile", label: "Profile Info" }, { id: "changePassword", label: "Change Password" }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${activeTab === tab.id ? "bg-blue-500 text-white shadow-lg" : "text-gray-400 hover:text-white"}`}>{tab.label}</button>
        ))}
      </div>

      {activeTab === "profile" && (
        <div className={`${card} border rounded-3xl p-8 shadow-sm`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-2xl bg-blue-500/15 flex items-center justify-center"><svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg></div>
            <h2 className={`font-display font-bold text-2xl ${text}`}>My Profile</h2>
          </div>

          {/* Avatar */}
          <div className={`flex flex-col items-center mb-8 pb-8 border-b ${isDarkMode ? "border-gray-800" : "border-slate-100"}`}>
            <div className="relative group mb-4">
              <div className="profile-ring w-32 h-32 absolute inset-0 scale-[1.04] opacity-70" />
              {profilePicture
                ? <img src={profilePicture} alt="Profile" className="relative w-32 h-32 rounded-full object-cover z-10" />
                : <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-5xl z-10">{userData.fullNames.charAt(0).toUpperCase()}</div>
              }
              <label htmlFor="profile-upload" className="absolute bottom-1 right-1 z-20 w-9 h-9 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 shadow-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </label>
              <input id="profile-upload" type="file" accept="image/*" onChange={handleProfilePictureUpload} className="hidden" />
            </div>
            <h3 className={`font-display font-bold text-2xl ${text}`}>{userData.fullNames}</h3>
            <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{userData.email}</p>
            {uploading && <div className="mt-3 flex items-center gap-2 text-blue-400 text-sm"><div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />Uploading...</div>}
            {uploadSuccess && <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Profile picture updated!</div>}
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            {[
              { label: "Full Name", value: userData.fullNames }, { label: "Email", value: userData.email },
              { label: "Username", value: userData.username }, { label: "Phone", value: userData.phone || "Not provided" },
              { label: "Address", value: userData.address || "Not provided" }, { label: "Language", value: userData.language === "en" ? "English" : userData.language },
              { label: "Registration Date", value: new Date(userData.registrationDate).toLocaleDateString() }, { label: "User ID", value: userData.identificationCode },
            ].map(item => (
              <div key={item.label} className="info-row">
                <p className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{item.label}</p>
                <p className={`text-sm font-semibold mt-0.5 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "changePassword" && (
        <div className={`${card} border rounded-3xl p-8 shadow-sm`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-2xl bg-orange-500/15 flex items-center justify-center"><svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg></div>
            <div><h2 className={`font-display font-bold text-2xl ${text}`}>Change Password</h2><p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Update your account credentials</p></div>
          </div>
          <div className="max-w-md space-y-4">
            {[{ val: newPassword, set: setNewPassword, ph: "New password" }, { val: confirmPassword, set: setConfirmPassword, ph: "Confirm new password" }].map((f, i) => (
              <input key={i} type="password" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph} className={`w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-blue-500/40 transition-all text-sm ${inputCls}`} />
            ))}
            <button onClick={handleChangePassword} disabled={passwordLoading} className="w-full py-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
              {passwordLoading ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Updating...</> : <>Update Password</>}
            </button>
            {passwordMessage && <div className={`flex items-center gap-2 text-sm font-medium ${passwordMessage.includes("success") ? "text-emerald-400" : "text-red-400"}`}>
              {passwordMessage.includes("success") ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              {passwordMessage}
            </div>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Settings View ──────────────────────────────────────────────────────────── */
const SettingsView = ({ userData, setCurrentView, isDarkMode }) => {
  const card = isDarkMode ? "bg-gray-900/80 border-gray-800/60" : "bg-white border-slate-200/60";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const sub = isDarkMode ? "text-gray-400" : "text-gray-500";
  const section = isDarkMode ? "bg-gray-800/60 border-gray-700/40" : "bg-slate-50 border-slate-200/60";

  return (
    <div className="max-w-3xl mx-auto animate-slide-up">
      <button onClick={() => setCurrentView("dashboard")} className={`mb-6 flex items-center gap-2 text-sm font-medium transition-all hover:-translate-x-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back
      </button>
      <div className={`${card} border rounded-3xl p-8 shadow-sm`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-slate-500/15 flex items-center justify-center"><svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
          <h2 className={`font-display font-bold text-2xl ${text}`}>Settings</h2>
        </div>
        <div className="space-y-4">
          {[
            {
              icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9", title: "Notifications", desc: "Manage your notification preferences",
              content: <label className="flex items-center gap-3 cursor-pointer group"><div className="relative"><input type="checkbox" defaultChecked className="sr-only" /><div className="w-10 h-6 bg-blue-500 rounded-full transition-colors" /><div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform translate-x-4" /></div><span className={`text-sm font-medium ${sub}`}>Email notifications</span></label>
            },
            {
              icon: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129", title: "Language", desc: "Choose your preferred language",
              content: <select className={`w-full max-w-xs px-4 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/40 transition-all ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-slate-200 text-gray-900"}`}><option value="en">🇺🇸 English</option><option value="es">🇪🇸 Spanish</option><option value="fr">🇫🇷 French</option></select>
            },
          ].map((item, i) => (
            <div key={i} className={`p-5 rounded-2xl border ${section}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-white shadow-sm"}`}><svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} /></svg></div>
                <div><h3 className={`font-semibold ${text}`}>{item.title}</h3><p className={`text-xs ${sub}`}>{item.desc}</p></div>
              </div>
              {item.content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;